// @wc-ignore-file
// cspell:ignore Ghislain Blouc blouch jeanpierre kairo alikes
/**
 * CR-215: turn a spoken mention in a dictated transcript ("at Eric please
 * review", "arobase Ghislain", "@kairo") into the same text the composer's @
 * picker inserts: `@First Last` for a teammate (the `getDisplayName` form the
 * send-time notification maps back to a user id) and `@handle` for an AI
 * persona (the token room-policy routes on).
 *
 * Pure and React-free. The directive above keeps wuchale away from the
 * trigger words, which are data compared against what the browser heard (a
 * translated "at" would silently stop matching under a non-English locale).
 *
 * Rules (CR-215 Q1–Q3 defaults):
 * - A trigger word of the dictation language, or a literal "@" (alone or glued
 *   to the name), only converts when a member or persona name follows it;
 *   "at the office" stays as spoken.
 * - A teammate is matched by full name, first name or last name; a persona by
 *   handle (with its - _ . read as spaces too) or display name. Case,
 *   diacritics, apostrophes and hyphens are folded.
 * - The longest matching name wins; a name that still fits several people, or
 *   fits nobody, leaves the words untouched (no picker, no error).
 * - Only when nothing matches exactly, an AI persona may match through its
 *   sound-alike key (`soundAlikeKey`, CR-215 S4: the recogniser writes the
 *   persona "Kairo" as "Cairo"). Teammate names never do: a misheard person
 *   is left as spoken, never guessed — and a word that also sounds like a
 *   teammate's name is left as spoken rather than handed to the AI.
 * - Running the resolver on its own output changes nothing, so the dictation
 *   hook can rewrite the whole transcript on every recognition event.
 */

import type { DictationLanguage } from "@/features/chat/voice-dictation";
import { getDisplayName } from "@/features/profiles/profile.utils";

const LETTER_OR_DIGIT = /[\p{L}\p{N}]/u;
const LETTERS_ONLY = /^\p{L}+$/u;

/**
 * The punctuation run at the end of `word` ("Anna,?" → ",?"). A plain scan, not
 * an anchored regex: names are user-chosen text of any length, and
 * `/[^\p{L}\p{N}]+$/` is quadratic on a long run of punctuation mid-word.
 */
function trailingPunctuation(word: string): string {
  const chars = Array.from(word);
  let end = chars.length;
  while (end > 0 && !LETTER_OR_DIGIT.test(chars[end - 1] ?? "")) {
    end -= 1;
  }
  return chars.slice(end).join("");
}

/** `word` without punctuation at either end (same linear scan). */
function trimPunctuation(word: string): string {
  const chars = Array.from(word);
  let start = 0;
  while (start < chars.length && !LETTER_OR_DIGIT.test(chars[start] ?? "")) {
    start += 1;
  }
  let end = chars.length;
  while (end > start && !LETTER_OR_DIGIT.test(chars[end - 1] ?? "")) {
    end -= 1;
  }
  return chars.slice(start, end).join("");
}

function normalizedSet(words: readonly string[]): ReadonlySet<string> {
  return new Set(words.map(normalizeSpokenToken));
}

type FixedDictationLanguage = Exclude<DictationLanguage, "browser">;

const FIXED_TRIGGERS: Record<FixedDictationLanguage, readonly string[]> = {
  en: ["at", "mention"],
  fr: ["arobase", "at", "mention", "mentionne"],
  de: ["at", "ät", "erwähne", "erwähnen"],
};

/** The spoken words that introduce a mention, per dictation language. */
export const SPOKEN_MENTION_TRIGGERS: Record<DictationLanguage, readonly string[]> = {
  ...FIXED_TRIGGERS,
  // The browser's own language can be any of them: accept the union.
  browser: [...new Set(Object.values(FIXED_TRIGGERS).flat())],
};

const NORMALIZED_TRIGGERS: Record<DictationLanguage, ReadonlySet<string>> = {
  en: normalizedSet(SPOKEN_MENTION_TRIGGERS.en),
  fr: normalizedSet(SPOKEN_MENTION_TRIGGERS.fr),
  de: normalizedSet(SPOKEN_MENTION_TRIGGERS.de),
  browser: normalizedSet(SPOKEN_MENTION_TRIGGERS.browser),
};

/**
 * One spoken word folded for comparison: lower case, no diacritics, no
 * apostrophes or hyphens ("Blouc'h" → "blouch", "Jean-Pierre" → "jeanpierre"),
 * no punctuation at either end.
 */
export function normalizeSpokenToken(raw: string): string {
  return trimPunctuation(
    raw
      .normalize("NFD")
      .replace(/\p{M}/gu, "")
      .toLowerCase()
      .replace(/['’ʼ`´-]/g, ""),
  );
}

/**
 * A folded word reduced to how it sounds, for persona near-matches only: the
 * small set of spellings a speech recogniser swaps for the same sound (ph → f,
 * ck and any q → k, c before e/i/y → s, any other c → k, y → i, doubled
 * letters collapsed). "ch" is left alone on purpose (it is soft in French
 * names). Deliberately not a general phonetic or edit-distance match, so
 * ordinary words keep their meaning. `token` is already `normalizeSpokenToken`
 * output.
 */
export function soundAlikeKey(token: string): string {
  return token
    .replace(/ph/g, "f")
    .replace(/ck|q/g, "k")
    .replace(/c(?=[eiy])/g, "s")
    .replace(/c/g, "k")
    .replace(/y/g, "i")
    .replace(/(.)\1+/g, "$1");
}

/** A persona alias with fewer letters than this never near-matches ("kai" vs "cai"). */
const MIN_SOUND_ALIKE_LETTERS = 4;

function toSoundAlike(alias: readonly string[]): string[] {
  return alias.map(soundAlikeKey);
}

/** A name as a sequence of folded words; empty when nothing is left. */
function toAlias(name: string): string[] {
  return name.split(/\s+/).map(normalizeSpokenToken).filter(Boolean);
}

export interface SpokenMentionCandidate {
  kind: "human" | "persona";
  /** user id for a teammate, persona id for a persona: identity for ambiguity. */
  id: string;
  /**
   * What the send path needs: the display name for a teammate (the key of the
   * composer's display-name → user-id map), the handle for a persona.
   */
  value: string;
  /** The text written into the composer, `@` included. */
  insert: string;
  /** Every spoken form that designates this candidate, as folded words. */
  aliases: string[][];
  /**
   * `aliases` through `soundAlikeKey`, tried when nothing matches exactly. A
   * persona can be matched this way (aliases of 4+ letters only); a
   * teammate's entries only make such a near-match ambiguous, so a teammate
   * is never matched by sound.
   */
  soundAlikes: string[][];
}

export interface SpokenMentionHumanInput {
  userId: string;
  firstName: string | null | undefined;
  lastName: string | null | undefined;
  email: string | null | undefined;
}

export interface SpokenMentionPersonaInput {
  personaId: string;
  handle: string | null | undefined;
  displayName: string | null | undefined;
}

function dedupeAliases(aliases: string[][]): string[][] {
  const seen = new Set<string>();
  return aliases.filter((alias) => {
    const key = alias.join(" ");
    if (alias.length === 0 || seen.has(key)) {
      return false;
    }
    seen.add(key);
    return true;
  });
}

/**
 * The candidates a transcript can mention. A teammate without a first or last
 * name (the picker shows their email) is skipped: an address cannot be said.
 */
export function buildSpokenMentionCandidates(input: {
  humans: readonly SpokenMentionHumanInput[];
  personas: readonly SpokenMentionPersonaInput[];
}): SpokenMentionCandidate[] {
  const result: SpokenMentionCandidate[] = [];
  for (const human of input.humans) {
    const first = human.firstName?.trim() ?? "";
    const last = human.lastName?.trim() ?? "";
    if (!first && !last) {
      continue;
    }
    const displayName = getDisplayName({ firstName: first, lastName: last, email: human.email });
    const humanAliases = dedupeAliases(
      [displayName, first, last].flatMap((name) => [
        toAlias(name),
        toAlias(name.replace(/-+/g, " ")),
      ]),
    );
    result.push({
      kind: "human",
      id: human.userId,
      value: displayName,
      insert: `@${displayName}`,
      // Hyphens fold away ("Jean-Pierre" → "jeanpierre"), but a recognizer
      // may as well hear two words: accept both forms.
      aliases: humanAliases,
      // Never used to match a teammate: only to block a persona near-match.
      soundAlikes: dedupeAliases(humanAliases.map(toSoundAlike)),
    });
  }
  for (const persona of input.personas) {
    const bare = persona.handle?.trim().replace(/^@+/, "") ?? "";
    if (!bare) {
      continue;
    }
    const handle = `@${bare}`;
    const aliases = dedupeAliases([
      toAlias(bare),
      toAlias(bare.replace(/[-_.]+/g, " ")),
      toAlias(persona.displayName ?? ""),
    ]);
    result.push({
      kind: "persona",
      id: persona.personaId,
      value: handle,
      insert: handle,
      aliases,
      soundAlikes: dedupeAliases(
        aliases
          .filter((alias) => alias.join("").length >= MIN_SOUND_ALIKE_LETTERS)
          .map(toSoundAlike),
      ),
    });
  }
  return result;
}

export interface ResolvedSpokenMentions {
  /** The transcript with every resolved mention rewritten. */
  text: string;
  /** Display names of the teammates mentioned, first occurrence order, unique. */
  humans: string[];
  /** Handles of the personas mentioned, first occurrence order, unique. */
  personas: string[];
}

interface NameMatch {
  candidate: SpokenMentionCandidate;
  /** Number of transcript words the name used. */
  length: number;
}

/** The transcript's words, each folded once and with its trailing punctuation. */
interface SpokenWords {
  folded: readonly string[];
  /** `folded` through `soundAlikeKey`, for the persona near-match pass. */
  soundAlike: readonly string[];
  trailing: readonly string[];
}

/**
 * The single candidate whose alias matches the words from `start`, longest
 * alias first; null when none matches or the longest match is ambiguous.
 * Exact aliases are tried first; only when none fits at all are the
 * sound-alike keys tried, and that pass may only yield a persona. A glued
 * "@eric" folds to "eric", so it matches like a separate word. A name never
 * runs across punctuation: only its last word may carry some.
 */
function matchName(
  words: SpokenWords,
  start: number,
  candidates: readonly SpokenMentionCandidate[],
): NameMatch | null {
  const exact = longestMatches(words.folded, words.trailing, start, candidates, "aliases");
  if (exact.candidates.length > 0) {
    return single(exact);
  }
  // Nothing fits exactly: a persona may still fit by sound ("Cairo" → @kairo),
  // unless a teammate sounds the same (then the words stay as spoken).
  const near = single(
    longestMatches(words.soundAlike, words.trailing, start, candidates, "soundAlikes"),
  );
  return near?.candidate.kind === "persona" ? near : null;
}

interface LongestMatches {
  candidates: SpokenMentionCandidate[];
  /** Number of transcript words the longest fitting alias used. */
  length: number;
}

/** Every candidate whose `field` alias fits `spoken` from `start`, longest only. */
function longestMatches(
  spoken: readonly string[],
  trailing: readonly string[],
  start: number,
  candidates: readonly SpokenMentionCandidate[],
  field: "aliases" | "soundAlikes",
): LongestMatches {
  let bestLength = 0;
  const best = new Map<string, SpokenMentionCandidate>();
  for (const candidate of candidates) {
    for (const alias of candidate[field]) {
      if (alias.length < bestLength || start + alias.length > spoken.length) {
        continue;
      }
      const fits = alias.every(
        (token, offset) =>
          spoken[start + offset] === token &&
          (offset === alias.length - 1 || trailing[start + offset] === ""),
      );
      if (!fits) {
        continue;
      }
      if (alias.length > bestLength) {
        bestLength = alias.length;
        best.clear();
      }
      best.set(`${candidate.kind}:${candidate.id}`, candidate);
    }
  }
  return { candidates: [...best.values()], length: bestLength };
}

/** The match when exactly one candidate fits, null when none or several do. */
function single(found: LongestMatches): NameMatch | null {
  const [candidate] = found.candidates;
  return found.candidates.length === 1 && candidate ? { candidate, length: found.length } : null;
}

function withoutTrailing(word: string): string {
  return word.slice(0, word.length - trailingPunctuation(word).length).toLowerCase();
}

/**
 * The candidate whose exact `@insert` token starts at `start` (case-insensitive,
 * punctuation after it allowed), longest first: an existing "@berg" stays the
 * persona @berg even when "berg" alone is also a teammate's last name. This is
 * what keeps the resolver idempotent on its own output.
 */
function matchInsert(
  rawWords: readonly string[],
  words: SpokenWords,
  start: number,
  candidates: readonly SpokenMentionCandidate[],
): NameMatch | null {
  let found: NameMatch | null = null;
  let tie = false;
  for (const candidate of candidates) {
    const tokens = candidate.insert.split(" ");
    const length = tokens.length;
    if (start + length > rawWords.length || (found && length < found.length)) {
      continue;
    }
    const fits = tokens.every((token, offset) => {
      const raw = rawWords[start + offset] ?? "";
      return offset === length - 1
        ? withoutTrailing(raw) === withoutTrailing(token)
        : words.trailing[start + offset] === "" && raw.toLowerCase() === token.toLowerCase();
    });
    if (!fits) {
      continue;
    }
    if (found && length === found.length && found.candidate !== candidate) {
      tie = true;
    } else if (!found || length > found.length) {
      found = { candidate, length };
      tie = false;
    }
  }
  return tie ? null : found;
}

/**
 * Rewrites the spoken mentions of `transcript` (see the module header for the
 * rules). Text outside a resolved mention, whitespace included, is kept
 * verbatim; punctuation right after the name stays after the inserted token.
 */
export function resolveSpokenMentions(
  transcript: string,
  candidates: readonly SpokenMentionCandidate[],
  language: DictationLanguage,
): ResolvedSpokenMentions {
  const humans: string[] = [];
  const personas: string[] = [];
  if (candidates.length === 0) {
    return { text: transcript, humans, personas };
  }
  const triggers = NORMALIZED_TRIGGERS[language];
  // [word, separator, word, separator, …, word]: words sit at even indices.
  const parts = transcript.split(/(\s+)/);
  const words = parts.filter((_, index) => index % 2 === 0);
  const separatorAfter = (wordIndex: number) => parts[wordIndex * 2 + 1] ?? "";
  const folded = words.map(normalizeSpokenToken);
  const spoken: SpokenWords = {
    folded,
    soundAlike: folded.map(soundAlikeKey),
    trailing: words.map(trailingPunctuation),
  };

  let out = "";
  let index = 0;
  while (index < words.length) {
    const word = words[index] ?? "";
    let match: NameMatch | null = null;
    let consumedBeforeName = 0;
    if (word.length > 1 && word.startsWith("@")) {
      // Glued "@eric" (or the resolver's own "@Éric Le Blouc'h" output).
      match = matchInsert(words, spoken, index, candidates) ?? matchName(spoken, index, candidates);
    } else if (
      (word === "@" || (LETTERS_ONLY.test(word) && triggers.has(spoken.folded[index] ?? ""))) &&
      // "look at @Anna": the mention is already there, the trigger is a word.
      !(words[index + 1] ?? "").startsWith("@")
    ) {
      consumedBeforeName = 1;
      if (index + 1 < words.length) {
        match = matchName(spoken, index + 1, candidates);
      }
    }

    if (!match) {
      out += word + separatorAfter(index);
      index += 1;
      continue;
    }

    const lastIndex = index + consumedBeforeName + match.length - 1;
    // A name ending in punctuation ("Smith Jr.") already carries it in its
    // insert: keep only what follows, or every pass would add another ".".
    const insert = match.candidate.insert;
    const ownTrailing = trailingPunctuation(insert);
    const spokenTrailing = spoken.trailing[lastIndex] ?? "";
    const trailing = spokenTrailing.startsWith(ownTrailing)
      ? spokenTrailing.slice(ownTrailing.length)
      : spokenTrailing;
    out += insert + trailing + separatorAfter(lastIndex);
    const bucket = match.candidate.kind === "human" ? humans : personas;
    if (!bucket.includes(match.candidate.value)) {
      bucket.push(match.candidate.value);
    }
    index = lastIndex + 1;
  }
  return { text: out, humans, personas };
}
